/**
 * Standardized Loading Components
 * 
 * These components provide consistent loading states across the application.
 * Use these instead of creating custom loading implementations.
 */

import * as React from "react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

// Standard loading text options
export const loadingMessages = {
  default: "Loading...",
  saving: "Saving...",
  creating: "Creating...",
  updating: "Updating...",
  deleting: "Deleting...",
  processing: "Processing...",
  uploading: "Uploading...",
  fetching: "Fetching data...",
  searching: "Searching...",
} as const;

// Page-level loading component
interface StandardPageLoadingProps {
  message?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

export function StandardPageLoading({ 
  message = loadingMessages.default, 
  size = "lg",
  className 
}: StandardPageLoadingProps) {
  const sizeClasses = {
    sm: "py-8",
    md: "py-12", 
    lg: "py-16"
  };

  return (
    <div className={cn(
      "flex flex-col items-center justify-center space-y-4",
      sizeClasses[size],
      className
    )}>
      <Spinner size={size} />
      <p className="text-muted-foreground text-sm font-medium">
        {message}
      </p>
    </div>
  );
}

// Table loading skeleton
interface StandardTableLoadingProps {
  rows?: number;
  columns?: number;
  className?: string;
}

export function StandardTableLoading({ 
  rows = 5, 
  columns = 4,
  className 
}: StandardTableLoadingProps) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex} className={cn("border-b", className)}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <td key={colIndex} className="p-4">
              <Skeleton className={cn(
                "h-4 rounded-md",
                colIndex === 0 ? "w-32" : "w-24" // First column wider
              )} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

// Card loading skeleton
interface StandardCardLoadingProps {
  hasHeader?: boolean;
  hasImage?: boolean;
  textLines?: number;
  className?: string;
}

export function StandardCardLoading({ 
  hasHeader = true,
  hasImage = false,
  textLines = 3,
  className 
}: StandardCardLoadingProps) {
  return (
    <Card className={cn("animate-pulse", className)}>
      {hasHeader && (
        <CardHeader className="space-y-2">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </CardHeader>
      )}
      <CardContent className="space-y-3">
        {hasImage && (
          <Skeleton className="h-48 w-full rounded-lg" />
        )}
        <div className="space-y-2">
          {Array.from({ length: textLines }).map((_, i) => (
            <Skeleton 
              key={i} 
              className={cn(
                "h-4",
                i === textLines - 1 ? "w-3/4" : "w-full"
              )} 
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Form loading skeleton
interface StandardFormLoadingProps {
  fields?: number;
  hasSubmitButton?: boolean;
  className?: string;
}

export function StandardFormLoading({ 
  fields = 4,
  hasSubmitButton = true,
  className 
}: StandardFormLoadingProps) {
  return (
    <div className={cn("space-y-6", className)}>
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" /> {/* Label */}
          <Skeleton className="h-10 w-full" /> {/* Input */}
        </div>
      ))}
      
      {hasSubmitButton && (
        <div className="flex justify-end pt-4">
          <Skeleton className="h-10 w-24" />
        </div>
      )}
    </div>
  );
}

// Dashboard metrics loading
interface StandardMetricsLoadingProps {
  count?: number;
  layout?: "grid" | "row";
  className?: string;
}

export function StandardMetricsLoading({ 
  count = 4,
  layout = "grid",
  className 
}: StandardMetricsLoadingProps) {
  const containerClass = layout === "grid" 
    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
    : "flex flex-col md:flex-row gap-4";

  return (
    <div className={cn(containerClass, className)}>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="p-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" /> {/* Label */}
            <Skeleton className="h-8 w-20" /> {/* Value */}
            <Skeleton className="h-3 w-24" /> {/* Change indicator */}
          </div>
        </Card>
      ))}
    </div>
  );
}

// List item loading skeleton
interface StandardListItemLoadingProps {
  hasAvatar?: boolean;
  hasStatus?: boolean;
  hasActions?: boolean;
  className?: string;
}

export function StandardListItemLoading({ 
  hasAvatar = true,
  hasStatus = true,
  hasActions = true,
  className 
}: StandardListItemLoadingProps) {
  return (
    <div className={cn(
      "flex items-center justify-between p-4 border-b",
      className
    )}>
      <div className="flex items-center gap-3 flex-1">
        {hasAvatar && (
          <Skeleton className="h-10 w-10 rounded-full" />
        )}
        <div className="space-y-2 flex-1">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-24" />
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {hasStatus && (
          <Skeleton className="h-6 w-16 rounded-full" />
        )}
        {hasActions && (
          <Skeleton className="h-8 w-8 rounded-md" />
        )}
      </div>
    </div>
  );
}

// Navigation loading skeleton
interface StandardNavigationLoadingProps {
  items?: number;
  hasIcons?: boolean;
  className?: string;
}

export function StandardNavigationLoading({ 
  items = 6,
  hasIcons = true,
  className 
}: StandardNavigationLoadingProps) {
  return (
    <div className={cn("space-y-2 p-4", className)}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-2">
          {hasIcons && (
            <Skeleton className="h-4 w-4" />
          )}
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

// Page header loading skeleton
interface StandardPageHeaderLoadingProps {
  hasSubtitle?: boolean;
  hasActions?: boolean;
  hasBackButton?: boolean;
  className?: string;
}

export function StandardPageHeaderLoading({ 
  hasSubtitle = true,
  hasActions = true,
  hasBackButton = false,
  className 
}: StandardPageHeaderLoadingProps) {
  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          {hasBackButton && (
            <Button variant="outline" disabled>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          )}
          <Skeleton className="h-8 w-48" /> {/* Title */}
          {hasSubtitle && (
            <Skeleton className="h-4 w-64" />
          )}
        </div>
        
        {hasActions && (
          <div className="flex gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-32" />
          </div>
        )}
      </div>
    </div>
  );
}

// Search results loading
interface StandardSearchLoadingProps {
  count?: number;
  hasFilters?: boolean;
  className?: string;
}

export function StandardSearchLoading({ 
  count = 5,
  hasFilters = true,
  className 
}: StandardSearchLoadingProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Search bar */}
      <div className="flex gap-2">
        <Skeleton className="h-10 flex-1" />
        <Skeleton className="h-10 w-24" />
      </div>
      
      {/* Filters */}
      {hasFilters && (
        <div className="flex gap-2">
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-8 w-16" />
        </div>
      )}
      
      {/* Results */}
      <div className="space-y-2">
        {Array.from({ length: count }).map((_, i) => (
          <StandardListItemLoading key={i} />
        ))}
      </div>
    </div>
  );
}

// Button loading state
interface StandardButtonLoadingProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  loadingText?: string;
  children: React.ReactNode;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
}

export function StandardButtonLoading({ 
  loading = false,
  loadingText,
  children,
  disabled,
  className,
  ...props 
}: StandardButtonLoadingProps) {
  return (
    <Button 
      disabled={disabled || loading}
      className={className}
      {...props}
    >
      {loading && <Spinner size="sm" className="mr-2" />}
      {loading && loadingText ? loadingText : children}
    </Button>
  );
}

// Mobile-optimized loading states
export function StandardMobilePageLoading({ 
  message = loadingMessages.default 
}: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 space-y-4">
      <Spinner size="lg" />
      <p className="text-muted-foreground text-sm text-center">
        {message}
      </p>
    </div>
  );
}

export function StandardMobileCardLoading() {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-12 w-12 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-6 w-6" />
      </div>
    </Card>
  );
}

export function StandardMobileListLoading({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <StandardMobileCardLoading key={i} />
      ))}
    </div>
  );
}

// Data view scaffold loading (for tables/lists)
interface StandardDataViewLoadingProps {
  mode?: "table" | "cards" | "list";
  count?: number;
  hasHeader?: boolean;
  hasFilters?: boolean;
  className?: string;
}

export function StandardDataViewLoading({ 
  mode = "table",
  count = 5,
  hasHeader = true,
  hasFilters = true,
  className 
}: StandardDataViewLoadingProps) {
  return (
    <div className={cn("space-y-4", className)}>
      {/* Page header */}
      {hasHeader && (
        <StandardPageHeaderLoading hasBackButton={false} />
      )}
      
      {/* Filters/Search */}
      {hasFilters && (
        <div className="flex gap-2">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-10 w-24" />
        </div>
      )}
      
      {/* Content based on mode */}
      {mode === "table" && (
        <div className="border rounded-lg">
          <div className="p-4 border-b">
            <StandardTableLoading rows={count} columns={4} />
          </div>
        </div>
      )}
      
      {mode === "cards" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: count }).map((_, i) => (
            <StandardCardLoading key={i} />
          ))}
        </div>
      )}
      
      {mode === "list" && (
        <div className="border rounded-lg">
          {Array.from({ length: count }).map((_, i) => (
            <StandardListItemLoading key={i} />
          ))}
        </div>
      )}
    </div>
  );
}